/**
 * カード — 謀略（ぼうりゃく）15枚
 *
 * docs/01-ゲームデザイン.md §9 の「謀略 15枚」に対応する。
 *
 * 【設計方針】
 * 合戦は docs/06-賭けと軍配勝負.md のとおりテキサスホールデムで解決する。
 * したがって timing: 'battle' / 'reaction' のカードは、
 *   - formationMod … 役（陣形）の段階を上げ下げする
 *   - holeCardMod  … 手勢札の枚数を増減する
 * のいずれかで効かせる。「サイコロ+1」のような旧仕様の効果は書かない。
 *
 * scope の読み方（core/effects.ts の Effect）:
 *   'attack' = 攻め手の役に作用 / 'defend' = 受け手の役に作用 / 'both' = 使用者の役に作用
 * 相手の役を崩す札は { t: 'target', who: 'chosen', then: [...] } で包み、
 * 対象プレイヤーの側に作用させる。
 *
 * 【執筆基準】docs/05-学習設計.md §4 と provinces/_reference.ts に揃える。
 *   - 武将を「英雄」「悪」と評価しない。戦闘行為を美化・賛美しない
 *   - 宗教勢力（一向一揆・キリスト教・比叡山）は事実の記述に留める
 *   - 女性・民衆の存在を消さない。境目の村・逃散・刈田の被害を必ず入れる
 *   - 通説と異説がある事項は caveat に必ず書く
 *   - ゲームの都合で史実を改変した箇所は gameNote に必ず書く
 *   - 年号は西暦を主とし、必要に応じて和暦を添える
 *
 * 【この15枚の内訳】
 *   人を動かす（5）  調略・国衆の去就・内応・寝返り・離間
 *   言葉と文書（3）  流言・贋書状・反間
 *   潜り込む（3）    乱波と透波・草・城の乗っ取り
 *   国を削る（2）    刈田・逃散と還住
 *   結びを作る（2）  一味神水・一揆との連携
 *
 * 【この15枚が扱う主題について】
 * 戦国の勢力争いは、合戦よりも交渉と離反によって動いた部分が大きい。
 * 一方で、その工作の被害をもっとも受けたのは境目に暮らす村の人々だった。
 * 謀略札は「巧い策」を並べる場ではなく、その両面を盤上に記録するために置く。
 */

import type { Card } from '../types';

// ---------------------------------------------------------------------------
// 人を動かす
// ---------------------------------------------------------------------------

/** 敵方の家臣・国衆を味方に引き入れる交渉。謀略札の基本形。 */
export const CHOURYAKU: Card = {
  id: 'card:bouryaku:chouryaku',
  name: '調略',
  reading: 'ちょうりゃく',
  type: 'bouryaku',
  cost: { zeni: 3 },
  timing: 'council',
  effects: [
    // 相手方の兵が離れ、こちらに付く
    { t: 'target', who: 'chosen', then: [{ t: 'lose', res: 'hei', n: 2 }] },
    { t: 'gain', res: 'hei', n: 2 },
  ],
  note: {
    summary:
      '敵方の家臣や国衆に働きかけて味方に引き入れる交渉。所領の安堵や加増を条件に、使者と書状を何度も往復させて進められた。',
    detail: {
      1: 'てきの家来に、こっそり使いを送って味方になってもらうことを調略といいます。土地をそのまま持たせる、ふやしてやる、といったやくそくをして話をまとめました。たたかわずに城や国が手に入ることもあったので、大名は手紙と使者を何度も送りました。',
      2: '敵方の家臣・国衆に働きかけて味方に引き入れる交渉を調略（調儀）という。大名は取次（とりつぎ）と呼ばれる担当の家臣を置き、僧侶や商人など境を越えて動ける者を使者に立てて書状を交わした。条件は本領の安堵、所領の加増、官途名の授与などで、合意は起請文の交換で確かめられた。1567年（永禄10年）、織田信長は美濃の稲葉一鉄・氏家卜全・安藤守就（西美濃三人衆）を味方につけ、まもなく稲葉山城を落として美濃を手に入れている。実際の勢力争いは、合戦よりも調略で動いた部分が大きい。',
      3: '調略は敵方の武将・国衆・城将に対して行われる離反工作で、同時代の史料では「調儀」「才覚」「手引」などとも記される。実務は取次と呼ばれる担当家臣が担い、僧侶・山伏・商人・医師など、境を越えて往来できる者が使者に用いられた。提示される条件は本領安堵、加増、官途・受領名の授与、人質の待遇などで、合意は起請文の交換によって確認された。1567年（永禄10年）の織田信長による美濃平定は、稲葉良通（一鉄）・氏家直元（卜全）・安藤守就のいわゆる西美濃三人衆を味方に付けたことが直接の契機とされ、斎藤龍興は稲葉山城を退いた。調略が成立するかどうかは当人の損得だけでなく、一族・被官・在地の村々の意向にも左右され、家中の合議を経て決まることが多い。調略に応じた者を「裏切り者」とする語り方は近世以降の武士道の観念に強く影響されており、当時の国衆にとって主家の選択は、家と在地を存続させるための現実的な判断でもあった。',
    },
    year: [1467, 1600],
    keywords: ['調略', '調儀', '取次', '西美濃三人衆', '起請文', '国衆'],
    caveat:
      '調略に応じた武将を「裏切り者」と評価する見方は、後の時代の価値観による部分が大きいものです。当時の国衆にとって、どの大名につくかは家と領民を守るための判断でした。',
    gameNote:
      '盤上では「相手の兵-2・自分の兵+2」と即座に処理していますが、実際の調略は使者と書状を何度も往復させる長い交渉で、合意しても実行されないことが多くありました。',
  },
};

/** 境目の在地領主が主家を選び直す。両属という事実を盤上に出す。 */
export const KUNISHU_NO_KYOSHU: Card = {
  id: 'card:bouryaku:kunishu',
  name: '国衆の去就',
  reading: 'くにしゅうのきょしゅう',
  type: 'bouryaku',
  cost: { zeni: 2, gunryaku: 1 },
  timing: 'council',
  effects: [
    { t: 'target', who: 'chosen', then: [{ t: 'lose', res: 'koku', n: 1 }] },
    { t: 'gain', res: 'koku', n: 1 },
    { t: 'gain', res: 'hei', n: 1 },
  ],
  note: {
    summary:
      '郡や谷ひとつほどを支配する在地領主（国衆）が、どの大名に従うかを選び直すこと。境目では両方の大名に従う「両属」も珍しくなかった。',
    detail: {
      1: '戦国のころ、村や小さな城をおさめる「国衆（くにしゅう）」とよばれる人たちがいました。大きな大名にしたがいますが、どちらにつくかは自分たちで決めます。国ざかいでは、二つの大名の両方にしたがうこともありました。家と土地を守るための、むずかしい判断でした。',
      2: '国衆（くにしゅう）は郡や谷ひとつほどの範囲を支配する在地領主で、戦国大名の指揮下に入りながら、自分の城・所領・家臣団を持っていた。大名に従うかどうかは家の存続がかかった判断であり、境目の国衆は情勢に応じて主家を替えた。信濃の真田氏は武田氏の滅亡後、織田・北条・徳川・上杉と従属先を替えている。両方の大名に同時に従う両属という形もあり、村が双方に年貢を納めたり、双方へ人質を出したりすることもあった。',
      3: '国衆は戦国期の在地領主層を指す研究上の呼称で、同時代の史料には「国衆」「他国衆」「外様衆」などと現れる。彼らは大名の軍事指揮下に入りつつ、独自の家臣団・城郭・所領支配を保持しており、大名との関係は一方的な主従というより、双方の利害にもとづく契約に近い性格を持った。したがって大名の敗勢が明らかになれば従属先を替えることは当然の選択肢であり、二大名の勢力が接する境目では、双方に人質を出し双方に年貢を納める両属の状態が生じた。信濃の真田氏は武田氏滅亡後、織田・北条・徳川・上杉と関係を組み替えながら沼田領を確保しようとしている。黒田基樹らの研究によって、戦国大名の権力は国衆との関係の上に成り立つものであり、大名が領国を一元的に支配していたとする古い理解は修正された。国衆の去就は個人の忠誠心の問題ではなく、家と在地の村を存続させるための判断として理解する必要がある。',
    },
    year: [1467, 1600],
    keywords: ['国衆', '両属', '境目', '真田氏', '在地領主', '所領'],
    caveat:
      '「国衆」は研究上定着した呼称で、同時代の史料では呼び方が一定しません。また、戦国大名が領国を一元的に支配していたという理解は近年見直されており、国衆との関係のうえに成り立つ権力だったと考えられています。',
    gameNote:
      '盤上では相手の石高を1減らし、自分が石高と兵を得る処理にしていますが、実際の国衆の離反は境目の一部で起きるもので、国がまるごと移るわけではありませんでした。',
  },
};

/** 城内に内通者を作る。攻め手の役が上がる。 */
export const NAIOU: Card = {
  id: 'card:bouryaku:naiou',
  name: '内応',
  reading: 'ないおう',
  type: 'bouryaku',
  cost: { zeni: 2 },
  timing: 'battle',
  effects: [
    // 内側から門が開く。攻め手の役が1段階上がる
    { t: 'holeCardMod', n: 1, scope: 'attack' },
  ],
  note: {
    summary:
      '城や陣の内部に味方を作り、合図にあわせて門を開かせたり火を放たせたりすること。攻める側の損害を大きく減らした。',
    detail: {
      1: 'せめる城の中に、みかたになってくれる人をつくっておくことを内応といいます。合図をきめておいて、門をあけてもらったり、火をつけてもらったりします。城は正面からせめると大きな損害が出るので、中に味方がいるかどうかで結果が大きくかわりました。',
      2: '城内や敵陣の内部に内通者を作り、開戦にあわせて門を開かせる、火を放つ、味方の位置を知らせるなどの働きをさせること。中世の城は堀・土塁・虎口を重ねて構築されており、力攻めをすれば攻め手に大きな損害が出た。そのため攻める側は事前に城将や城内の侍・足軽に働きかけ、合図と手順を取り決めた。内応は調略の一部として行われ、成功すればほとんど戦わずに城が落ちる。逆に露見すれば内通者は処罰され、大名のもとに預けた人質が殺されることもあった。',
      3: '内応は敵の城郭や陣所の内部に協力者を確保し、攻撃の時機にあわせて開門・放火・情報提供などを行わせる工作を指す。中世城郭は堀・土塁・切岸・虎口の組み合わせによって寡兵でも守れるように設計されており、力攻めによる攻略は攻撃側に甚大な損害をもたらした。このため城攻めの実際は、包囲と兵粮攻め、そして内応工作の組み合わせで進むことが多い。働きかけの相手は城将本人とは限らず、城内に詰める在地の侍衆や、普請・炊事にあたる人夫にまで及んだ。合図には狼煙・鬨の声・特定の刻限が用いられ、事前に起請文を取り交わす例もある。内応が露見した場合、当人だけでなく大名のもとに置かれた人質が処刑されることがあり、人質の存在は内応を抑止する装置でもあった。軍記が「一日で城が落ちた」と記す事例の多くは、こうした事前の工作を前提としている。',
    },
    year: [1467, 1600],
    keywords: ['内応', '手引', '城攻め', '虎口', '人質', '調略'],
    caveat:
      '軍記物は城が短時間で落ちたことを武将の采配の妙として描きますが、その多くは事前の内応工作を前提としています。合戦の記述を戦術の巧拙だけで読むと実態を見誤ります。',
    gameNote:
      '盤上では「攻め手の役が1段階上がる」としています（docs/06 §4）。内応が露見して失敗する可能性や、人質が処罰される結果は、遊びやすさのため省いています。',
  },
};

/** 合戦の場での離反。関ヶ原の小早川秀秋を、近年の研究を含めて扱う。 */
export const NEGAERI: Card = {
  id: 'card:bouryaku:negaeri',
  name: '寝返り',
  reading: 'ねがえり',
  type: 'bouryaku',
  cost: { zeni: 3, gunryaku: 1 },
  timing: 'battle',
  effects: [
    // 相手の陣から兵が抜ける。手勢札が1枚減る
    { t: 'target', who: 'chosen', then: [{ t: 'holeCardMod', n: -1 }] },
  ],
  note: {
    summary:
      '合戦の直前や最中に陣営を移ること。1600年の関ヶ原の戦いで小早川秀秋が東軍に付いた例が広く知られるが、その時点をめぐっては研究が分かれる。',
    detail: {
      1: 'いくさの前や、いくさのさいちゅうに、みかたから相手がわにうつることを寝返りといいます。1600年の関ヶ原の戦いでは、小早川秀秋という大名が徳川家康のがわについて、いくさのゆくえが決まりました。ただし、いつ心を決めたのかについては、いろいろな考えがあります。',
      2: '合戦の直前や最中に陣営を移ること。1600年（慶長5年）の関ヶ原の戦いでは、松尾山に布陣していた小早川秀秋が徳川家康方（東軍）として動き、石田三成方（西軍）の大谷吉継の陣を攻めた。従来は、家康が催促のために鉄砲を撃たせたこと（問鉄砲）で秀秋が決心したと語られてきたが、これは後世に編まれた史料に見える話で、同時代の史料には確認できない。近年の研究では、秀秋は合戦の前から東軍と通じており、開戦とほぼ同時に動いたとする見方が有力になっている。',
      3: '寝返りは、合戦の直前あるいは進行中に所属陣営を変更する行為を指す。関ヶ原の戦い（1600年9月15日）における小早川秀秋の行動が最もよく知られ、松尾山の秀秋勢が東軍として大谷吉継隊に攻めかかったことが西軍崩壊の契機となった。近世の軍記類は、秀秋が正午近くまで去就を決めかね、徳川家康が鉄砲を撃ちかけて催促したとする「問鉄砲」の逸話を伝えるが、この記述は同時代史料に確認できない。白峰旬らの研究は、合戦直後の状況を伝える書状の記載から、秀秋が開戦とほぼ同時に東軍として行動したと論じており、そもそも合戦の最中に大軍が陣営を変えることは指揮統制の上できわめて困難だという指摘もある。寝返りは当人の性格の問題として語られがちだが、実際には事前の調略、家中の合議、人質の有無、所領の保証といった条件の積み重ねの上に生じるものであった。',
    },
    year: 1600,
    keywords: ['寝返り', '関ヶ原の戦い', '小早川秀秋', '問鉄砲', '調略', '松尾山'],
    caveat:
      '関ヶ原で徳川家康が小早川秀秋に鉄砲を撃ちかけて催促したという「問鉄砲」の話は、後の時代に編まれた史料に見えるもので、同時代の史料では確認できません。秀秋は合戦の前から東軍と通じていたとする見方が近年は有力です。',
    gameNote:
      '盤上では「相手の手勢札を1枚減らす」としています（docs/06 §4）。合戦の途中で兵の一部が離れることを札の枚数で表した処理で、実際の寝返りは合戦が始まる前に段取りが決まっているのが普通でした。',
  },
};

/** 主従の間に疑いを生じさせる。江良房栄の例は軍記由来なので caveat が要る。 */
export const RIKAN: Card = {
  id: 'card:bouryaku:rikan',
  name: '離間',
  reading: 'りかん',
  type: 'bouryaku',
  cost: { gunryaku: 2 },
  timing: 'council',
  effects: [
    {
      t: 'target',
      who: 'chosen',
      then: [
        { t: 'lose', res: 'gunryaku', n: 2 },
        { t: 'lose', res: 'ishin', n: 1 },
      ],
    },
  ],
  note: {
    summary:
      '敵方の主君と重臣の間に疑いを生じさせ、内部から力を削ぐ工作。毛利元就が陶方の重臣を疑わせたと伝えられる例が知られる。',
    detail: {
      1: '敵の大名と、その家来のあいだをわるくさせるやりかたです。「あの家来はうらぎるつもりだ」といううわさを流したり、にせの手紙を見せたりします。仲がわるくなれば、たたかう前に敵の力がよわくなります。',
      2: '敵方の主従や家臣どうしの関係に疑いを生じさせ、内部から結束を崩す工作。『孫子』にも見える古い手法で、偽の書状、内通の噂、贈り物の見せかけなどが用いられた。1555年（天文24年）3月、大内氏の重臣であった陶晴賢の家臣・江良房栄が岩国で誅殺されている。従来の説では、毛利元就が房栄に内応をもちかけたうえで、その事実をあえて晴賢の側に伝えたためとされる。ただしこの経緯を詳しく描くのは後世の軍記物で、どこまでが事実かははっきりしない。',
      3: '離間は敵方の内部に不信を生じさせ、指揮系統や結束を弱める工作を指す。『孫子』用間篇の反間の思想に連なるもので、手段としては偽書状の作成、内通の風聞の流布、贈答の見せかけ、捕虜の意図的な解放などがあった。よく引かれるのが天文24年（1555）3月、陶晴賢の重臣・江良房栄が岩国で弘中隆兼によって誅殺された事件である。毛利元就が房栄に内応を働きかけたものの、房栄が加増を求めて交渉が難航したため、元就がその内応の事実を晴賢側に伝えて誅殺させた、という理解が示されてきた。ただし、この一連の経緯を物語として整えて伝えるのは享保年間に成立した『陰徳太平記』などの軍記であり、同書は毛利氏の側から書かれた後世の編纂物であるため、細部をそのまま史実とすることはできない。同年10月の厳島の戦いに至る過程で陶方の結束が緩んでいたこと自体は、複数の史料から裏づけられる。',
    },
    year: 1555,
    keywords: ['離間', '江良房栄', '陶晴賢', '毛利元就', '陰徳太平記', '厳島'],
    caveat:
      '毛利元就が江良房栄を離間によって除いたという経緯は、享保年間に成立した『陰徳太平記』など後世の軍記が詳しく伝えるもので、そのまま史実とはできません。元就を「謀の神」と呼ぶ評価も後世に定着した言い方です。',
    gameNote:
      '盤上では「相手の軍略-2・威信-1」としています。軍略が減るのは、家中の意思疎通が乱れて判断が鈍ることを表した処理で、離間そのものが数値を持っていたわけではありません。',
  },
};

// ---------------------------------------------------------------------------
// 言葉と文書
// ---------------------------------------------------------------------------

/** 噂を流す。中世の情報の流れ方（口頭・不確実）を書く。 */
export const RUGEN: Card = {
  id: 'card:bouryaku:rugen',
  name: '流言',
  reading: 'るげん',
  type: 'bouryaku',
  cost: { zeni: 1 },
  timing: 'anytime',
  effects: [{ t: 'target', who: 'chosen', then: [{ t: 'lose', res: 'ishin', n: 2 }] }],
  note: {
    summary:
      '噂を意図的に流して敵方の人心を揺さぶること。中世の情報は口伝えが中心で、市や宿、寺社の門前が噂の集まる場所だった。',
    detail: {
      1: 'うそやうわさをわざと広めて、敵の人たちの心をみだすやりかたです。「あの大名はもうまけそうだ」といううわさが広まると、みかたの気持ちがぐらつきます。うわさは、市や宿場、お寺の門前など、人があつまる場所から広がっていきました。',
      2: '意図的に噂を流して敵方の人心を動揺させる工作。中世の社会では文書だけでなく口伝えの「風聞」「取沙汰」が情報の主要な流通経路であり、市・宿・湊・寺社の門前など人が集まる場所が噂の結節点となった。商人・僧・修験者・芸能者など各地を往来する人々は、その運び手でもあった。京都では政治や世相を風刺した落書（らくしょ）が匿名で掲げられる伝統があり、1334年（建武元年）の二条河原落書が有名である。噂は自国の民衆にも及ぶため、大名が流言を取り締まる例もあった。',
      3: '流言は虚実を交えた情報を意図的に流布し、敵方の士気・結束・住民の動向に影響を与える工作である。中世日本の情報環境は口頭伝達の比重が高く、「風聞」「取沙汰」「巷説」といった語で史料に現れる。情報の担い手は、商人・連雀商人・僧侶・山伏・比丘尼・芸能者など、身分と境界を越えて移動できる人々であり、市・宿・湊・寺社門前が結節点となった。噂は合戦の帰趨にも実際に作用し、大名の死や敗報の風聞が兵の離散や国衆の離反を招いた例は少なくない。他方、匿名の批判文である落書は、建武元年（1334）の二条河原落書のように、政治への民衆の視線を伝える史料としても読まれてきた。流言の効果は制御しがたく、自領の百姓が動揺して逃散に至ることもあったため、大名は流言や落書を禁じる法を出している。戦国の工作を「情報戦」という近代的な語でまとめると、当時の情報が口頭中心で不確実、かつ地域限定であったという前提が見落とされやすい。',
    },
    year: [1334, 1600],
    keywords: ['流言', '風聞', '取沙汰', '落書', '二条河原落書', '市'],
    caveat:
      '戦国大名の情報活動を近代の「情報戦」になぞらえる説明は便利ですが、当時の情報は口伝えが中心で、正確さも伝わる速さも地域によって大きく異なりました。二条河原落書は1334年のもので、戦国時代より前の例です。',
    gameNote:
      '盤上では「相手の威信-2」としています。噂が広まる速さや、噂が自分の側にも跳ね返る危うさは、遊びやすさのため省いています。',
  },
};

/** 偽文書。文書が権利と命令そのものだった社会を書く。 */
export const GAN_SHOJOU: Card = {
  id: 'card:bouryaku:ganshojou',
  name: '贋書状',
  reading: 'がんしょじょう',
  type: 'bouryaku',
  cost: { gunryaku: 1 },
  timing: 'reaction',
  effects: [
    // 内通を疑われた側は動きが鈍る。相手の役が1段階下がる
    { t: 'target', who: 'chosen', then: [{ t: 'holeCardMod', n: -1, scope: 'both' }] },
  ],
  note: {
    summary:
      '本物に似せて作った書状。所領の権利も軍事の命令も文書で示された社会では、偽文書は実際に効力を持つことがあり、大名は花押や印判で真正さを示した。',
    detail: {
      1: 'ほんものそっくりにつくった、にせの手紙です。むかしは、手紙や書きつけが土地の持ち主をきめたり、命令をつたえたりする大切なものでした。だからこそ、にせものをつくって相手をこまらせることもありました。大名は、はんこや花押（かおう）というしるしをつけて、本物だと分かるようにしました。',
      2: '本物に似せて作られた書状・文書。中世社会では所領の権利も軍事の命令も文書によって示されたため、文書の真偽は現実の力関係を左右した。寺社や在地の領主が権利を主張するために古い文書を作り直す例は多く、偽文書は中世史料学の重要な研究対象になっている。軍事の場面では、敵方の重臣が内通しているかのように見せかける書状を作って疑いを生じさせる工作も行われた。これに対し大名は、花押（署名を図案化したもの）や印判（後北条氏の虎朱印など）を用い、書式を定めて文書の真正さを保証しようとした。',
      3: '偽文書は中世を通じて広く作成された。荘園の権利、寺社の由緒、村の入会権などを主張するために後の時代に整えられた「古文書」は数多く、近代以降の史料学はそれを見分ける方法を発達させてきた。軍事・外交の局面でも文書の偽造は用いられ、敵方の重臣が内通しているかのように見せかける書状を作って主君に届ける、通信を偽装して部隊を誘い出すといった工作が行われた。文書が権威そのものであった社会において、偽造は単なる詐術ではなく、実際に権利や命令を生み出しうる行為だった。これに対抗するため、大名は花押の形を定め、印判状を制度化する。後北条氏の虎朱印は伝馬の徴発や年貢の徴収に用いられ、「この印のない要求には応じなくてよい」と村に周知することで、家臣による恣意的な徴発と偽の命令の双方を抑える機能を持った。文書の真贋を判定する営みと、それを掻いくぐろうとする営みは、中世を通じて併走している。',
    },
    year: [1467, 1600],
    keywords: ['偽文書', '花押', '印判状', '虎朱印', '後北条氏', '史料学'],
    caveat:
      '偽文書を「だますための道具」とだけ理解すると実態を見誤ります。寺社や村が自分たちの権利を守るために由緒を書き整えた例も多く、文書の真偽の判定と、作った人々の意図とは分けて考える必要があります。',
    gameNote:
      '盤上では「相手の役を1段階下げる」としています（docs/06 §4）。偽書状の効き目を合戦の一場面に限定した処理で、実際の偽文書は合戦よりも所領や権利の主張の場で問題になりました。',
  },
};

/** 敵の間者を逆用する。『孫子』と近世軍学の関係に触れる。 */
export const HANKAN: Card = {
  id: 'card:bouryaku:hankan',
  name: '反間',
  reading: 'はんかん',
  type: 'bouryaku',
  cost: { gunryaku: 1 },
  timing: 'reaction',
  effects: [
    { t: 'gain', res: 'gunryaku', n: 2 },
    { t: 'target', who: 'chosen', then: [{ t: 'lose', res: 'gunryaku', n: 1 }] },
  ],
  note: {
    summary:
      '敵の放った間者を捕らえ、あるいは正体を知ったうえで泳がせ、偽の知らせを持ち帰らせること。『孫子』用間篇に説かれ、日本でも兵書として読まれた。',
    detail: {
      1: '敵がおくりこんできた間者（かんじゃ＝ようすを探る人）を見つけて、こらしめるのではなく、わざとうその知らせを持ちかえらせるやりかたです。中国の古い兵法書『孫子』に書かれていて、日本の武士も読んでいました。',
      2: '敵の間者を捕らえ、あるいは正体を知ったうえで泳がせ、偽の情報を持ち帰らせることを反間という。『孫子』用間篇は間者を郷間・内間・反間・死間・生間の五つに分け、なかでも反間を重んじた。『孫子』を含む中国の兵書は五山の禅僧を通じて日本にも広まり、戦国期には武将が講義を受けたり注釈が作られたりしている。ただし日本の合戦の実際が兵書のとおりに動いたわけではなく、兵書の知識と現場の慣行は別のものとして見る必要がある。',
      3: '反間は『孫子』用間篇に見える語で、敵の放った間者を掌握し、逆用して偽情報を伝えさせる手法を指す。同篇は間者を郷間（敵地の住民）・内間（敵方の官吏）・反間（敵の間者）・死間（偽情報を持たせ死地に送る者）・生間（往復して報告する者）の五つに分類し、情報活動を戦の要と位置づけている。『孫子』を含む武経七書は中世日本にも伝わり、五山の禅僧が講説し、戦国期には大名や家臣が学んだ形跡がある。ただし、これを体系的な「軍学」として整理したのは近世に入ってからで、武田氏の旧臣とされる小幡景憲が伝えた『甲陽軍鑑』や、山鹿素行・北条氏長らの軍学書がその中心となった。したがって「戦国武将が孫子を実践していた」という説明には、近世の軍学が整えた像を戦国に投影している面がある。実際の戦国の情報活動は、兵書の体系よりも、在地の人間関係と往来する人々に依存する、より泥臭いものであった。',
    },
    year: [1467, 1600],
    keywords: ['反間', '孫子', '用間篇', '間者', '軍学', '甲陽軍鑑'],
    caveat:
      '「戦国武将は『孫子』を実践していた」という説明は、江戸時代に発達した軍学の整理を戦国時代にあてはめている面があります。『甲陽軍鑑』も近世初頭に成立した書物です。',
    gameNote:
      '盤上では「自分の軍略+2・相手の軍略-1」としています。間者を逆に使うことの利を軍略という資源に置き換えた処理で、情報が正しく伝わったかどうかを判定するしくみは持っていません。',
  },
};

// ---------------------------------------------------------------------------
// 潜り込む
// ---------------------------------------------------------------------------

/** 乱波・透波。忍者像が後世の創作である点を caveat で必ず断る。 */
export const RAPPA_SUPPA: Card = {
  id: 'card:bouryaku:rappa',
  name: '乱波と透波',
  reading: 'らっぱとすっぱ',
  type: 'bouryaku',
  cost: { zeni: 2 },
  timing: 'anytime',
  effects: [
    { t: 'draw', n: 1 },
    { t: 'gain', res: 'gunryaku', n: 1 },
  ],
  note: {
    summary:
      '敵地に入って探索・放火・待ち伏せにあたった者たち。東国では乱波、西国では透波などと呼ばれ、後北条氏に仕えた風間（風魔）の名は同時代の文書にも見える。',
    detail: {
      1: '敵の国にしのびこんで、ようすを調べたり、火をつけてさわぎを起こしたりする人たちがいました。東の国では乱波（らっぱ）、西の国では透波（すっぱ）などとよばれます。北条氏につかえた風間（かざま）という一族の名前が、そのころの記録にのこっています。まんがやテレビに出てくる忍者とは、ずいぶんちがう人たちでした。',
      2: '戦国期に大名が召し抱えた、探索・放火・待ち伏せ・城の乗っ取りなどにあたった者たちを、地域によって乱波（らっぱ）・透波（すっぱ）・素破・かまり・草などと呼んだ。武田氏の三ツ者、上杉氏の軒猿といった呼称も知られるが、これらは後世の記録によるものが多い。後北条氏に仕えた風間（風魔とも書く）は同時代の文書にも名が見え、夜討ちや放火を担ったことが分かる。伊賀・甲賀の地侍は、村ごとの取り決めを持ちながら各地の大名に雇われた。彼らの多くは専業の「忍者」ではなく、在地の侍や百姓であった。',
      3: '戦国期の諜報・攪乱要員は、史料上、乱波・透波・素破・かまり・草・伏嗅（ふせかぎ）など多様な呼称で現れ、統一された職名はない。任務は敵地の偵察、放火、夜討ち、待ち伏せ、城の乗っ取り、道案内など広く、平山優『戦国の忍び』（2020）は各地の一次史料からこれらの活動を跡づけている。後北条氏に仕えた風間（風魔）については、同時代の文書に活動が見え、『北条五代記』など近世の記録が伝える無法者集団としての性格とおおむね符合するとされる。伊賀・甲賀では地侍が惣（そう）を結んで共同の掟を持ち、外部の大名と契約して傭兵的に働いた例が知られる。これらの人々の身分は流動的で、村の百姓、山の民、海の民との境界も明確ではない。一方、身の丈が二メートルを超え牙が生えていたといった風魔像や、黒装束・手裏剣・忍術といった図像は、近世の軍記・読本と近代以降の大衆文化が形づくったもので、戦国期の実態とは切り離して考える必要がある。',
    },
    year: [1467, 1600],
    keywords: ['乱波', '透波', '風間', '伊賀', '甲賀', '忍び'],
    caveat:
      '黒装束・手裏剣・忍術といった「忍者」の姿は、江戸時代の読み物と近代以降の映画や漫画が作り上げたものです。武田氏の三ツ者、上杉氏の軒猿などの呼称も後世の記録に多く見えるもので、同時代の史料で確認できることは限られています。',
    gameNote:
      '盤上では「カードを1枚引く・軍略+1」としています。情報を得ることを札の補充で表した処理で、探索が失敗する可能性は省いています。',
  },
};

/** 草を臥せる。境目の村の被害を落とさない。守る側の役が上がる。 */
export const KUSA: Card = {
  id: 'card:bouryaku:kusa',
  name: '草',
  reading: 'くさ',
  type: 'bouryaku',
  cost: { zeni: 1 },
  timing: 'battle',
  effects: [
    // 通り道に伏せておく。受け手の役が1段階上がる
    { t: 'holeCardMod', n: 1, scope: 'defend' },
  ],
  note: {
    summary:
      '敵の通り道に少人数を伏せておき、機を見て襲うこと。「草を臥せる」といい、後北条氏や武田氏の文書に「草調儀」の語が見える。',
    detail: {
      1: '敵が通りそうな道のわきに、人をかくしておくことを「草を臥せる（ふせる）」といいました。草むらにひそんで待ち、通りかかった敵をおそいます。国ざかいでは、こうしたことがたびたび起きていて、おそわれたのは兵だけでなく、田畑に出た村の人たちのこともありました。',
      2: '敵地や国境に少人数を潜ませ、通行する軍勢や物資を襲う戦法を「草」といい、そのための作戦を草調儀（くさちょうぎ）と呼んだ。後北条氏や武田氏の文書に語が見え、伏兵・伏勢とほぼ重なる。境目の地域では、草を臥せる側と、それを見つけ出す「草を狩る」側の攻防が日常的に繰り返された。標的は軍勢だけでなく、物資を運ぶ人夫や田畑に出た村人にも及んだため、境目の村は堀や柵で自衛し、危険な時期には山城や寺に避難した。',
      3: '「草」は、敵地や境目に少人数を潜伏させ、通行する軍勢・伝令・物資を襲撃する戦法およびその実行者を指す語で、後北条氏・武田氏をはじめとする東国の文書に「草調儀」「草を臥す」「草を起こす」といった表現で現れる。伏兵・伏勢とほぼ同義に用いられ、平山優『戦国の忍び』はこれを戦国期の忍びの実態の中核と位置づけている。草の任務は待ち伏せのほか、敵陣の監視、放火、人を捕らえることにも及んだ。捕らえた人を身代金と引き換えに返す例や、売買の対象とした例が史料に見える。防ぐ側は「草を狩る」ための番を置き、村には夜間の警戒と通報が課された。境目の村は堀や土塁で囲まれ、危険が迫れば山城や寺院に避難している。合戦の記録に残るのは大規模な野戦だが、境目に暮らす人々にとっての日常の脅威はむしろこの種の小規模な襲撃であり、その被害の多くは戦う立場にない人々に及んだ。',
    },
    year: [1467, 1600],
    keywords: ['草', '草調儀', '伏兵', '後北条氏', '境目', '村'],
    caveat:
      '「草」は特別な忍術ではなく、待ち伏せと監視という具体的な任務でした。被害を受けたのが軍勢だけでなく、境目に暮らす村の人々でもあった点を落とさないよう注意が要ります。',
    gameNote:
      '盤上では「守る側の役が1段階上がる」としています（docs/06 §4）。実際の草は守りだけでなく敵地での襲撃にも使われましたが、盤上では待ち伏せの側面だけを取り出しています。',
  },
};

/** 少人数で城を奪う。竹中重治の例は同時代史料が乏しい点を必ず書く。 */
export const SHIRO_NO_NOTTORI: Card = {
  id: 'card:bouryaku:nottori',
  name: '城の乗っ取り',
  reading: 'しろののっとり',
  type: 'bouryaku',
  cost: { zeni: 4, gunryaku: 2 },
  timing: 'council',
  effects: [
    // 兵を損なわずに国を切り取る
    { t: 'seize', free: true },
  ],
  note: {
    summary:
      '少人数で城内に入り込み、内側から城を奪うこと。1564年に竹中重治が稲葉山城を奪った話が知られるが、直接記した同時代の文書はほとんど残っていない。',
    detail: {
      1: '少ない人数で城の中に入りこみ、中から城をうばってしまうやりかたです。1564年、竹中重治（半兵衛）という人が、わずかな家来とともに美濃の稲葉山城をうばったと伝えられています。ただし、そのころに書かれた記録はほとんど残っておらず、くわしい話は後の時代の本によるものです。',
      2: '少人数で城内に潜入し、内部から城を制圧する方法。永禄7年（1564年）2月、美濃の斎藤龍興に仕えていた竹中重治（半兵衛）は、舅の安藤守就と示し合わせ、人質として城内にいた弟の見舞いと称して家臣とともに登城し、稲葉山城を奪ったと伝えられる。占拠は夏まで続き、8月ごろには龍興が城を取り戻したとみられる。ただし、この出来事を直接記した同時代の文書はほとんどなく、まとまった物語として伝えるのは後世の軍記や地誌である。城の乗っ取りは特別な武芸ではなく、城内に伝手を持つことと、警固の隙をつく段取りによって成り立った。',
      3: '城郭の奪取には、包囲・力攻め・兵粮攻めのほかに、少人数の潜入によって内部から制圧する方法があり、戦国期の史料には各地でこの種の事例が見える。最も有名なのが竹中重治（半兵衛）による稲葉山城奪取である。永禄7年（1564）2月、斎藤龍興の家臣であった重治は、舅の安藤守就と連携し、人質として城内にいた弟の見舞いを口実に少人数で登城して城を制圧し、龍興を城外へ退去させたと伝えられる。占拠は同年夏まで続き、8月ごろには龍興が城を回復したとみられる。動機については、家臣から侮辱を受けたことへの意趣、龍興の行状への諫言、舅の安藤守就をめぐる家中の争いへの助力など複数の説明が伝えられており、一定しない。問題はこの事件を直接記録した同時代文書がほとんど残らないことで、整った物語として現れるのは『美濃国諸旧記』など近世の編纂物である。したがって事件の骨格は認めつつ、細部の逸話は後世の潤色を含むものとして扱うのが穏当である。城の乗っ取りは、城内に縁者や被官を持つこと、警固の交替の刻限を知ることなど、在地の人間関係に強く依存する行為であった。',
    },
    year: 1564,
    keywords: ['城の乗っ取り', '竹中重治', '稲葉山城', '安藤守就', '斎藤龍興', '美濃'],
    caveat:
      '竹中重治の稲葉山城乗っ取りについては、出来事を直接記した同時代の文書がほとんど残っていません。詳しい経緯を伝えるのは『美濃国諸旧記』など後世に編まれた書物で、動機についても複数の説明が伝わっています。',
    gameNote:
      '盤上では「兵を使わずに国を切り取る」という強い効果にしており、そのぶん銭と軍略を多く払う設計にしています。実際の乗っ取りは成功しても長くは保てず、稲葉山城も半年ほどで斎藤方に戻っています。',
  },
};

// ---------------------------------------------------------------------------
// 国を削る
// ---------------------------------------------------------------------------

/** 敵地の作物を刈る。被害を受けたのが誰だったかを必ず書く（docs/05 §4-4）。 */
export const KARITA: Card = {
  id: 'card:bouryaku:karita',
  name: '刈田',
  reading: 'かりた',
  type: 'bouryaku',
  cost: { zeni: 1 },
  timing: 'anytime',
  effects: [
    { t: 'target', who: 'chosen', then: [{ t: 'lose', res: 'koku', n: 2 }] },
    // 村々の反発を受け、評判は下がる
    { t: 'lose', res: 'ishin', n: 1 },
  ],
  note: {
    summary:
      '敵地の田畑の作物を実る前に刈り取り、または薙ぎ倒して兵粮を断つ行為。合戦の常套手段だったが、被害を受けたのは村の人々だった。',
    detail: {
      1: '敵の国に入って、まだ実っていない麦や稲を刈ってしまうことです。相手の食べものをへらすためにおこなわれました。こまったのは兵だけではなく、その田畑をたがやしていた村の人たちでした。家をやかれたり、つれ去られたりすることもありました。',
      2: '敵地の田畑の作物を刈り取り、または薙ぎ倒す行為。麦の時期に行うものを麦薙ぎともいう。敵の兵粮を減らし、村を大名から離反させる狙いがあり、戦国期の軍事行動として頻繁に行われた。「刈田狼藉」は中世法の用語で、他人の田の稲を勝手に刈る不法行為を指し、鎌倉幕府以来、取り締まりの対象とされていた。合戦に伴う略奪は作物にとどまらず、家財の強奪や放火、人を捕らえて身代金を取ったり売買したりする乱妨取りにも及んだ。藤木久志の研究は、こうした行為が雑兵たちの収入源でもあった実態を明らかにしている。',
      3: '刈田は敵地の農作物を収穫期前に刈り取り、または踏み荒らして兵粮の供給を断つ軍事行動で、麦の季節に行うものを麦薙ぎと呼ぶ。目的は敵の補給の遮断と、村を敵方の大名から引き離す圧力の両方にあった。用語としての「刈田狼藉」は中世法上の犯罪概念であり、他人の所領の作物を実力で刈り取る行為を指して、鎌倉幕府の法でも守護の取締対象とされたが、戦国期の合戦では公然と行われた。被害を受けたのは第一に村の百姓であり、藤木久志『雑兵たちの戦場』は、合戦に随行した雑兵が作物・家財・人そのものを奪う乱妨取りによって収入を得ていた実態を、複数の史料から描き出した。捕らえられた人は身代金と引き換えに戻される場合もあれば、売買されて他国へ送られる場合もあった。豊臣秀吉は天正15年（1587）のいわゆる伴天連追放令のなかで日本人の売買にも触れており、人の売買が当時の政治的な問題として認識されていたことが分かる。村の側も無力ではなく、山城や寺への避難、他領への一時的な逃散、礼銭を納めて大名から制札（乱妨を禁じる高札）を得るなど、身を守る手段を持っていた。',
    },
    year: [1467, 1600],
    keywords: ['刈田', '麦薙ぎ', '乱妨取り', '制札', '雑兵', '村'],
    caveat:
      '戦国の合戦を武将の戦術としてのみ語ると、刈田や乱妨取りの被害を受けた村の人々の経験が見えなくなります。一方で、村が制札を得たり避難したりして身を守った事実もあり、一方的な被害者としてのみ描くことも実態とは異なります。',
    gameNote:
      '盤上では「相手の石高-2・自分の威信-1」としています。威信が下がるのは村々の反発と評判の低下を表した処理です。この札は略奪を勧めるために置いているのではなく、合戦がもたらした被害を盤上に記録するために置いています。',
  },
};

/** 逃散と還住。百姓が交渉の主体でありえたことを書く。 */
export const CHOUSAN_GENJUU: Card = {
  id: 'card:bouryaku:genjuu',
  name: '逃散と還住',
  reading: 'ちょうさんとげんじゅう',
  type: 'bouryaku',
  cost: { zeni: 2 },
  timing: 'council',
  effects: [
    { t: 'target', who: 'chosen', then: [{ t: 'lose', res: 'koku', n: 1 }] },
    { t: 'gain', res: 'koku', n: 2 },
  ],
  note: {
    summary:
      '百姓が村を捨てて他所へ移ること（逃散）と、荒れた村へ人を呼び戻すこと（還住）。大名は年貢の減免を示して、他領の百姓を招くこともあった。',
    detail: {
      1: '年貢が重すぎたり、いくさで村があぶなくなったりすると、村の人たちは村をはなれて、ほかの土地へうつりました。これを逃散（ちょうさん）といいます。人がいなくなると年貢が入らないので、大名は「年貢を軽くするから戻ってきてほしい」と呼びかけました。これを還住（げんじゅう）といいます。',
      2: '逃散は、百姓が村を離れて他所へ移ること。年貢の重さや代官の非法に抗議する手段としても行われ、要求が通れば戻るという交渉の形をとることもあった。戦乱で村が危険になれば、集団で山や他領へ避難した。人がいなくなれば年貢は入らないため、大名にとって百姓の確保は死活問題であり、荒れた村へ人を呼び戻す還住が政策として行われた。年貢や諸役の一定期間の免除、農具や種籾の貸与などを条件に他領の百姓を招くこともあり、これは相手方の力を削ぐ働きも持った。',
      3: '逃散は、百姓が耕地と屋敷を捨てて他所へ移動する行為で、年貢・夫役の過重や代官の非法に対する抵抗の手段として中世を通じて行われた。全村的な逃散は領主に対する強い圧力となり、要求が容れられれば還住するという交渉の構造を持つ。一方、戦乱による逃散は交渉ではなく避難であり、村ごと山中や他領、寺社の境内へ移った例が記録されている。人の減少は年貢収取の基盤を直接に損なうため、戦国大名は還住を促す政策をとった。具体的には、一定期間の年貢・諸役の免除、農具や種籾の貸与、家屋の普請の援助などである。これらの条件は他領の百姓を引き寄せる効果も持ち、境目では相手方の人を吸収する競争が生じた。逃散と還住をめぐる文書は、百姓が領主に対する交渉の主体でありえたことを示す史料として重視されている。村の労働と家計を支えたのは男女双方であり、逃散するかどうかの判断も家と村の単位で行われた。中世の百姓が土地に縛りつけられて動けなかったという理解は一面的で、移動の余地は確かに存在した。',
    },
    year: [1400, 1600],
    keywords: ['逃散', '還住', '百姓', '年貢減免', '村', '境目'],
    caveat:
      '中世の百姓が土地に縛りつけられて動けなかったという理解は、近年の研究では見直されています。逃散のように移動そのものを交渉の手段として使う余地があり、大名の側も人を集めるために条件を示していました。',
    gameNote:
      '盤上では「相手の石高-1・自分の石高+2」としています。人の移動を石高の増減に置き換えた処理で、実際には人が移ってきても、荒れた田が元の生産力を取り戻すまでには年月がかかりました。',
  },
};

// ---------------------------------------------------------------------------
// 結びを作る
// ---------------------------------------------------------------------------

/**
 * 一味同心を確かめる作法。寝返り・離間への備えとして使えるようにする。
 * 誓約文書そのもの（起請文の様式・神文・血判・熊野牛王宝印）は
 * 外交札 card:gaikou:kishoumon が扱う。こちらは「対等な結合を結ぶ儀礼」
 * という視点に限定し、記述が重ならないようにする。
 */
export const ICHIMI_SHINSUI: Card = {
  id: 'card:bouryaku:ichimishinsui',
  name: '一味神水',
  reading: 'いちみしんすい',
  type: 'bouryaku',
  cost: { gunryaku: 1 },
  timing: 'reaction',
  effects: [
    { t: 'gain', res: 'ishin', n: 1 },
    // 味方の離反を抑える。自分の役が1段階上がる
    { t: 'holeCardMod', n: 1, scope: 'both' },
  ],
  note: {
    summary:
      '心を一つにすることを誓い合う作法。誓いの文書を焼いた灰を神前の水に混ぜ、一同で回し飲んで対等な結びを確かめた。国人・村・家中のいずれでも行われた。',
    detail: {
      1: '目的を同じくする人たちが力をあわせることを一揆（いっき）といいます。仲間になるときは、神社にあつまってちかいの文書を書き、それをやいた灰を神さまにそなえた水にまぜて、みんなで回し飲みしました。これを一味神水（いちみしんすい）といいます。同じ水を分けあうことで、上下のない仲間だと確かめたのです。村の人も武士も、同じようにこの作法をしました。',
      2: '目的を共有する者が契約を結んで対等に行動することを一味同心（いちみどうしん）といい、その結合を一揆と呼んだ。結成にあたっては、誓う内容を記した文書（一揆契状）に参加者が連署し、これを社頭で焼いて灰を神水（神前に供えた水）に混ぜ、一同で回し飲む作法がとられた。これが一味神水である。神仏を証人として同じ水を分け合うことで、主従ではない横の結びであることを確かめた。国人が結ぶ国人一揆、惣村の百姓が村掟を定める寄合、家臣どうしが連署して誓う場面など、身分を問わず行われている。1485年（文明17年）の山城の国一揆では、国人と土民が宇治の平等院に集まって国中掟法を定め、以後8年ほど自治的な運営が続いた。',
      3: '一揆は暴動を指す語ではなく、目的を共有する者どうしが契約によって取り結ぶ横の結合そのもの、すなわち一味同心を意味する。その結成の作法が一味神水である。まず一揆契状（一味の誓約文書）を作り参加者が署名する。署名は列記の形が一般的だが、円形に据える形式もあり、近世の百姓一揆で知られる傘連判（からかされんぱん）はその系譜に位置づけられる。次にこの文書を神社の社頭で焼き、灰を神前の神水に混ぜて一同で回し飲む。誓約文書の末尾には違背した者が受ける神仏の罰が書かれており、その水を体内に取り込むことで、破れば自らが罰を受ける身になるという論理を参加者全員が共有した。適用範囲は広く、国人一揆のほか、惣村の寄合や宮座の掟、家中の連署による誓約にも及ぶ。文明17年（1485）、南山城では畠山両軍の退去を求めて国人と土民が宇治平等院に集会し、国中掟法を定めて以後8年ほど自治的な運営を行った。この結合は本来的に対等で、参加者は「一味の衆」として同格に扱われる。したがって一揆を大名が上から結ばせるものとして描くことはできず、同じ作法が大名家中の結束の確認にも用いられた点に、この時代の「結び」のかたちがよく表れている。',
    },
    year: [1400, 1600],
    keywords: ['一味神水', '一味同心', '一揆契状', '傘連判', '惣村', '山城の国一揆'],
    caveat:
      '「一揆」を暴動や反乱の意味で理解するのは近世以降に広まった用法で、中世では契約による対等な結合そのものを指す語でした。また、署名を円形に並べて主導者を分からなくする傘連判は近世の百姓一揆で広く見られる形式で、中世の一揆契状は署名を列記する形が一般的です。神水を飲む作法の細部は史料によって異なり、全国一律の手順があったわけではありません。',
    gameNote:
      '盤上では「威信+1・自分の役が1段階上がる」とし、寝返りや離間への備えとして使えるようにしています（docs/06 §4）。実際の一味神水に合戦の強さを変える力はなく、味方の離反を抑える働きをルールに翻訳したものです。一揆は大名が上から結ばせるものではなく、これを大名の一手として札にしているのは遊びの上の簡略化です。',
  },
};

/** 一揆との連携。宗教勢力は事実の記述に留める（docs/05 §4-3）。 */
export const IKKI_RENKEI: Card = {
  id: 'card:bouryaku:ikki',
  name: '一揆との連携',
  reading: 'いっきとのれんけい',
  type: 'bouryaku',
  cost: { zeni: 2, gunryaku: 1 },
  timing: 'council',
  effects: [
    { t: 'target', who: 'chosen', then: [{ t: 'lose', res: 'hei', n: 3 }] },
    // 一揆は大名の駒ではない。思惑どおりに動くとは限らず、評判にも響く
    { t: 'lose', res: 'ishin', n: 1 },
  ],
  note: {
    summary:
      '敵大名の領内で起きている一揆と書状を交わし、背後で動いてもらうこと。1570年代の織田信長と本願寺の対立には、複数の大名が関わっていた。',
    detail: {
      1: 'そのころ、目的を同じくする人たちがちかいを立てて力をあわせる「一揆」がありました。大名は、敵の国で起きている一揆と手紙をやりとりして、敵のうしろがわで動いてもらうことがありました。一揆は大名の家来ではないので、思ったとおりに動くとはかぎりません。',
      2: '一揆は、共通の目的のために契約を結んで行動する集団やその行動を指す語で、国人一揆・土一揆・一向一揆などがある。加賀では1488年（長享2年）に浄土真宗本願寺派の門徒と国人が守護富樫政親を高尾城に攻め、以後およそ100年にわたって門徒・国人・寺院による支配が続いた。1570年（元亀元年）に始まる石山合戦では本願寺と織田信長が長く対立し、その間、朝倉義景・浅井長政・武田信玄・毛利輝元らが本願寺と書状を交わしている。大名どうしの争いと宗教勢力の動きは、こうして結びついていた。',
      3: '一揆とは、目的を共有する者が契約（一味同心）を結んで行動する結合の形と、その行動を指す。戦国期には在地領主が結ぶ国人一揆、徳政などを求める土一揆、浄土真宗本願寺派の門徒による一向一揆などがあった。加賀では長享2年（1488）、門徒と国人が守護富樫政親を高尾城に攻めて自害させ、以後、天正8年（1580）に織田信長方が制圧するまで、門徒・国人・寺院による支配が続いた。元亀元年（1570）に始まる石山合戦では本願寺顕如が各地の門徒に呼びかけ、伊勢長島や越前でも大規模な蜂起が起きている。この間、朝倉義景・浅井長政・武田信玄・毛利輝元ら信長と敵対する大名は本願寺と書状を交わし、軍事的な連携を図った。すなわち一向一揆は、信仰にもとづく運動であると同時に、大名間の政治・軍事の力学のなかに置かれていた。天正2年（1574）の伊勢長島、天正3年（1575）の越前での鎮圧では、戦う立場にない人々を含む多数が殺害されたと記録されている。宗教勢力の行動は「抵抗」「反乱」のいずれか一方に還元せず、門徒・寺院・国人・大名それぞれの利害が交錯した結合として見る必要がある。',
    },
    year: [1488, 1580],
    keywords: ['一揆', '一向一揆', '加賀', '石山合戦', '本願寺', '国人'],
    caveat:
      '一向一揆を「農民の宗教的な反乱」とのみ説明するのは正確ではありません。国人（在地領主）や寺院を含む複合的な結合で、加賀の支配も門徒と国人の連合によるものでした。宗教勢力の行動については、評価ではなく事実の記述にとどめる必要があります。',
    gameNote:
      '盤上では「相手の兵-3・自分の威信-1」としています。一揆は大名が動かせる駒ではなく、それぞれの目的を持って行動していました。連携を大名の側の一手として札にしているのは、遊びの上の簡略化です。',
  },
};

// ---------------------------------------------------------------------------
// まとめ
// ---------------------------------------------------------------------------

/** 謀略カード 15枚。 */
export const BOURYAKU_CARDS: readonly Card[] = [
  // 人を動かす
  CHOURYAKU,
  KUNISHU_NO_KYOSHU,
  NAIOU,
  NEGAERI,
  RIKAN,
  // 言葉と文書
  RUGEN,
  GAN_SHOJOU,
  HANKAN,
  // 潜り込む
  RAPPA_SUPPA,
  KUSA,
  SHIRO_NO_NOTTORI,
  // 国を削る
  KARITA,
  CHOUSAN_GENJUU,
  // 結びを作る
  ICHIMI_SHINSUI,
  IKKI_RENKEI,
];
